import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/apiService';
import axios from 'axios';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Fab,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PostAddIcon from '@mui/icons-material/PostAdd';
import CloseIcon from '@mui/icons-material/Close';
import AnalyticProfileForm from './AnalyticProfileForm';
import { ApiError } from '../../errors/ApiError';
import { UserContext } from '../../contexts/UserContext';
import { AnalyticProfile } from '../../types/analytic-profile';
import { formatBackendErrors } from '../../utils/errorUtils';
import { formatAnalyticProfileCreatedAt, normalizeAnalyticProfilesResponse } from '../../utils/analyticProfilesResponse';
type PanelMode = 'create' | 'view' | 'edit';

const formatApiError = (err: ApiError): string => {
  const { status, data } = err;
  if (status === 400 && typeof data === 'object' && data !== null) {
    return Object.entries(data)
      .map(([field, message]) => `${field}: ${message}`)
      .join('\n');
  }
  if (status === 404) return 'The requested resource was not found.';
  if (status === 500) return 'Something went wrong on our end. Please try again.';
  return `Unexpected error (status ${status ?? 'unknown'}). Please try again.`;
};

export const AnalyticProfilesList = () => {
  const { user } = useContext(UserContext);
  const ap = user?.permissions?.analytic_profiles;
  const canRead = Boolean(ap?.read);
  const canCreate = Boolean(ap?.create);

  const [profiles, setProfiles] = useState<AnalyticProfile[]>([]);
  const [isLoading, setIsLoading] = useState(canRead);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [forceDeleteModal, setForceDeleteModal] = useState<{ profileId: string; message: string } | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<PanelMode>('view');
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [drawerTitle, setDrawerTitle] = useState<string | null>(null);

  const fetchProfiles = useCallback(async () => {
    if (!canRead) return;
    try {
      setIsLoading(true);
      setError(null);
      const res = await api.get('/api/analytic-profiles/all');
      const normalized = normalizeAnalyticProfilesResponse(res);
      setProfiles(normalized as unknown as AnalyticProfile[]);
    } catch {
      setError('Failed to fetch analytic-profiles');
    } finally {
      setIsLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    if (canRead) {
      void fetchProfiles();
    } else {
      setIsLoading(false);
    }
  }, [canRead, fetchProfiles]);

  const openDrawer = (mode: PanelMode, profileId: string | null) => {
    setPanelMode(mode);
    setSelectedProfileId(profileId);
    setDrawerTitle(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedProfileId(null);
    setDrawerTitle(null);
  };

  const handleViewProfile = (id: string) => openDrawer('view', id);
  const handleEditProfile = (id: string) => openDrawer('edit', id);

  const handleDeleteProfile = async (id: string) => {
    try {
      await api.delete(`/api/analytic-profiles/${id}`);
      setProfiles((prev) => prev.filter((profile) => profile.id !== id));
      setDeleteError(null);
    } catch (err) {
      const is400 = err instanceof ApiError ? err.status === 400 : axios.isAxiosError(err) && err.response?.status === 400;
      const data =
        err instanceof ApiError
          ? err.data
          : (axios.isAxiosError(err) ? err.response?.data : undefined) as Record<string, string> | undefined;
      const usageMessage = data?.usage;

      if (is400 && usageMessage?.includes('force=true')) {
        const userFacingMessage = usageMessage.replace(/\.?\s*Use force=true to unassign and delete\.?/i, '').trim();
        setForceDeleteModal({ profileId: id, message: userFacingMessage });
        return;
      }

      const parsedError =
        err instanceof ApiError
          ? formatApiError(err)
          : axios.isAxiosError(err)
            ? formatBackendErrors(err.response || null)
            : 'We failed to delete the analytic profile';
      setDeleteError(parsedError);
    }
  };

  const handleForceDeleteConfirm = async () => {
    if (!forceDeleteModal) return;
    const { profileId } = forceDeleteModal;
    setForceDeleteModal(null);
    try {
      await api.delete(`/api/analytic-profiles/${profileId}?force=true`);
      setProfiles((prev) => prev.filter((profile) => profile.id !== profileId));
      setDeleteError(null);
    } catch (err) {
      const parsedError =
        err instanceof ApiError
          ? formatApiError(err)
          : axios.isAxiosError(err)
            ? formatBackendErrors(err.response || null)
            : 'We failed to delete the analytic profile';
      setDeleteError(parsedError);
    }
  };

  const toolbarTitle = useMemo(() => {
    if (panelMode === 'create') return 'Create analytic profile';
    return drawerTitle ?? 'Profile details';
  }, [panelMode, drawerTitle]);

  const formKey = `${panelMode}-${selectedProfileId ?? 'new'}`;

  if (canRead && isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress size={60} sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  if (canRead && error) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <Alert
          severity="error"
          sx={{
            bgcolor: '#27272a',
            color: '#f87171',
            border: '1px solid #3f3f46',
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {deleteError && (
        <Alert
          severity="error"
          onClose={() => setDeleteError(null)}
          sx={{
            mb: 2,
            bgcolor: '#27272a',
            color: '#f87171',
            border: '1px solid #3f3f46',
          }}
        >
          <AlertTitle sx={{ color: '#f87171' }}>Error</AlertTitle>
          {deleteError}
        </Alert>
      )}
      <Dialog
        open={!!forceDeleteModal}
        onClose={() => setForceDeleteModal(null)}
        PaperProps={{
          sx: {
            bgcolor: '#0a0a0a',
            border: '1px solid #27272a',
            color: '#fafafa',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fafafa' }}>Profile in use</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#a1a1aa' }}>{forceDeleteModal?.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setForceDeleteModal(null)} sx={{ color: '#a1a1aa' }}>
            Cancel
          </Button>
          <Button
            onClick={handleForceDeleteConfirm}
            variant="contained"
            sx={{
              bgcolor: '#f87171',
              color: '#0a0a0a',
              '&:hover': { bgcolor: '#ef4444' },
            }}
          >
            Unassign and delete
          </Button>
        </DialogActions>
      </Dialog>

      {!canRead && canCreate && (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: 'center',
            border: '1px dashed #3f3f46',
            borderRadius: 2,
            color: '#a1a1aa',
            mb: 2,
          }}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            You do not have access to the analytic profiles directory.
          </Typography>
          <Typography variant="body2" sx={{ color: '#71717a' }}>
            Use the button below to create a new profile.
          </Typography>
        </Box>
      )}

      {canRead && (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          bgcolor: '#111111',
          border: '1px solid #27272a',
        }}
      >
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#141414', '& th': { borderColor: '#27272a' } }}>
              <TableCell sx={{ color: '#a1a1aa', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderColor: '#27272a' }}>
                NAME
              </TableCell>
              <TableCell sx={{ color: '#a1a1aa', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderColor: '#27272a' }}>
                DESCRIPTION
              </TableCell>
              <TableCell sx={{ color: '#a1a1aa', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderColor: '#27272a' }}>
                CREATED
              </TableCell>
              <TableCell align="right" sx={{ width: 120, borderColor: '#27272a' }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={() => void fetchProfiles()} sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ borderColor: '#27272a', py: 5, textAlign: 'center', color: '#71717a' }}>
                  No analytic profiles yet.
                  {canCreate ? ' Use + to add the first profile.' : ''}
                </TableCell>
              </TableRow>
            )}
            {profiles.map((profile) => (
              <TableRow
                key={profile.id}
                hover
                sx={{
                  '& td': { borderColor: '#27272a', py: 1.5 },
                  '&:last-of-type td': { borderBottom: 0 },
                }}
              >
                <TableCell sx={{ color: '#fafafa', fontWeight: 500, borderColor: '#27272a' }}>{profile.name}</TableCell>
                <TableCell sx={{ color: '#d4d4d8', borderColor: '#27272a', maxWidth: 360 }}>{profile.description}</TableCell>
                <TableCell sx={{ color: '#a1a1aa', borderColor: '#27272a' }}>
                  {formatAnalyticProfileCreatedAt(profile.created_at)}
                </TableCell>
                <TableCell align="right" sx={{ borderColor: '#27272a' }}>
                  <Box display="flex" justifyContent="flex-end" gap={0.25}>
                    {user?.permissions?.analytic_profiles?.read && (
                      <Tooltip title="View">
                        <IconButton
                          onClick={() => handleViewProfile(profile.id)}
                          size="small"
                          sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {user?.permissions?.analytic_profiles?.update && (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleEditProfile(profile.id)}
                          size="small"
                          sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {user?.permissions?.analytic_profiles?.delete && (
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => void handleDeleteProfile(profile.id)}
                          size="small"
                          sx={{ color: '#a1a1aa', '&:hover': { color: '#f87171' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {canCreate && (
        <Fab
          color="primary"
          aria-label="create analytic profile"
          onClick={() => openDrawer('create', null)}
          sx={{
            position: 'fixed',
            right: { xs: 20, sm: 28 },
            bottom: { xs: 20, sm: 28 },
            zIndex: (theme) => theme.zIndex.drawer - 1,
            bgcolor: '#60a5fa',
            color: '#0a0a0a',
            boxShadow: '0 8px 24px rgba(96, 165, 250, 0.35)',
            '&:hover': { bgcolor: '#3b82f6' },
          }}
        >
          <PostAddIcon />
        </Fab>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '50vw' },
            maxWidth: { xs: '100%', sm: '50vw' },
            minWidth: { sm: 360 },
            bgcolor: '#0a0a0a',
            borderLeft: '1px solid #27272a',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            px: 2,
            minHeight: 56,
            borderBottom: '1px solid #27272a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 600, flex: 1, minWidth: 0 }}>
            {toolbarTitle}
          </Typography>
          <IconButton
            edge="end"
            onClick={closeDrawer}
            aria-label="close panel"
            sx={{ color: '#a1a1aa', '&:hover': { color: '#fafafa' } }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {drawerOpen && (panelMode === 'create' || selectedProfileId) && (
            <AnalyticProfileForm
              key={formKey}
              mode={panelMode}
              profileId={selectedProfileId ?? undefined}
              embedded
              onClose={closeDrawer}
              onSuccess={() => void fetchProfiles()}
              onHeaderDisplayNameChange={setDrawerTitle}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};
